/**
 * Dashboard Screen
 *
 * Tela principal do admin com métricas
 */
import React from 'react'
import {
	Users,
	Building2,
	Calendar,
	Star,
	MessageSquare,
	TrendingUp,
} from 'lucide-react'
import Header from '../organisms/Header'
import StatCard from '../molecules/StatCard'
import GrowthChart from '../organisms/GrowthChart'
import ActivityFeed from '../organisms/ActivityFeed'
import Card from '../atoms/Card'
import { LoadingScreen } from '../atoms/Spinner'
import { useDashboardMetrics } from '../../core/admin.hooks'
import { formatDate } from '../../core/admin.utils'

export default function DashboardScreen() {
	const { overview, growth, activity, loading, error, refetch } = useDashboardMetrics()

	if (loading) {
		return (
			<div className="min-h-screen">
				<Header title="Dashboard" />
				<LoadingScreen />
			</div>
		)
	}

	if (error) {
		return (
			<div className="min-h-screen">
				<Header title="Dashboard" />
				<div className="p-6">
					<Card className="text-center py-12">
						<p className="text-red-600 mb-4">{error}</p>
						<button
							onClick={refetch}
							className="text-primary-600 hover:underline"
						>
							Tentar novamente
						</button>
					</Card>
				</div>
			</div>
		)
	}

	// Formatar dados do gráfico
	const chartData = growth?.users?.map((item, idx) => ({
		date: formatDate(item.date, 'dd/MM'),
		users: item.count || 0,
		clubs: growth?.clubs?.[idx]?.count || 0,
		matches: growth?.matches?.[idx]?.count || 0,
	})) || []

	// Formatar atividades - A API retorna array direto com { type, message, data, createdAt }
	const formattedActivities = (activity || []).map((item, idx) => {
		// Mapear tipo da API para tipo do componente
		const typeMap = {
			user_created: 'user',
			club_created: 'club',
			match_created: 'match',
			rating_created: 'rating',
		}

		return {
			id: item.data?._id || `activity-${idx}`,
			type: typeMap[item.type] || 'user',
			message: item.message,
			createdAt: item.createdAt,
		}
	})

	return (
		<div className="min-h-screen">
			<Header
				title="Dashboard"
				subtitle={`Visão geral do FutJá • ${formatDate(new Date(), "dd 'de' MMMM 'de' yyyy")}`}
			/>

			<div className="p-6 space-y-6">
				{/* Stats Grid */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
					<StatCard
						title="Total de Usuários"
						value={overview?.users?.total || 0}
						icon={Users}
						iconColor="green"
						changeLabel={`+${overview?.users?.new7Days || 0} últimos 7 dias`}
					/>
					<StatCard
						title="Clubes Ativos"
						value={overview?.clubs?.total || 0}
						icon={Building2}
						iconColor="blue"
						changeLabel="total"
					/>
					<StatCard
						title="Partidas"
						value={overview?.matches?.total || 0}
						icon={Calendar}
						iconColor="purple"
						changeLabel={`${overview?.matches?.completed || 0} concluídas`}
					/>
					<StatCard
						title="Partidas Agendadas"
						value={overview?.matches?.scheduled || 0}
						icon={TrendingUp}
						iconColor="yellow"
						changeLabel="aguardando"
					/>
				</div>

				{/* Secondary Stats */}
				<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
					<StatCard
						title="Avaliações"
						value={overview?.ratings?.total || 0}
						icon={Star}
						iconColor="yellow"
						changeLabel={`Média: ${overview?.ratings?.average || 0}`}
					/>
					<StatCard
						title="Feedbacks"
						value={overview?.feedbacks?.total || 0}
						icon={MessageSquare}
						iconColor="blue"
						changeLabel={`Média: ${overview?.feedbacks?.average || 0}`}
					/>
					<StatCard
						title="Usuários Ativos"
						value={overview?.users?.active || 0}
						icon={Users}
						iconColor="purple"
						changeLabel="últimos 30 dias"
					/>
				</div>

				{/* Charts and Activity */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					<div className="lg:col-span-2">
						<GrowthChart
							data={chartData}
							title="Crescimento nos últimos 30 dias"
						/>
					</div>
					<div>
						<ActivityFeed
							activities={formattedActivities}
							title="Atividade Recente"
							maxItems={8}
						/>
					</div>
				</div>
			</div>
		</div>
	)
}
